import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  markOrganizationContextReconciled,
  ORGANIZATION_CONTEXT_RECOVERED_EVENT,
  setOrganizationReconcilerMounted,
  setOrganizationWriteRefusedMessage,
} from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useSession } from "./useSession";

/**
 * Follow the API client when it adopts another organization after the stored
 * one was refused (SEC-D41): reload the session, drop every cached read of the
 * old organization and tell the person. Writes are never resent; the refused
 * one carries a translated message asking to repeat it.
 */
export function OrganizationContextRecovery() {
  const queryClient = useQueryClient();
  const { refreshSession } = useSession();
  const { t } = useI18n();

  useEffect(() => {
    setOrganizationWriteRefusedMessage(() => t("tenant.recovery.writeRefused"));
  }, [t]);

  useEffect(() => {
    setOrganizationReconcilerMounted(true);
    return () => setOrganizationReconcilerMounted(false);
  }, []);

  useEffect(() => {
    const onRecovered = () => {
      void (async () => {
        try {
          await queryClient.cancelQueries();
          await refreshSession();
          // The reset drops the old reads at once; their refetch may take
          // its time without holding writes.
          void queryClient.resetQueries();
        } catch (error) {
          console.warn(
            "[tenant] reload after an organization change failed",
            error,
          );
        } finally {
          markOrganizationContextReconciled();
        }
        toast.warning(t("tenant.recovery.notice"));
      })();
    };
    window.addEventListener(ORGANIZATION_CONTEXT_RECOVERED_EVENT, onRecovered);
    return () =>
      window.removeEventListener(
        ORGANIZATION_CONTEXT_RECOVERED_EVENT,
        onRecovered,
      );
  }, [queryClient, refreshSession, t]);

  return null;
}
